import React, {
  useState,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Dropdown,
  ProgressBar,
} from 'react-bootstrap';

import { useHistory } from 'react-router-dom';

import CsLineIcons from 'cs-line-icons/CsLineIcons';

import { settingsChangeColor } from 'settings/settingsSlice';

import { IReduxState } from 'ReduxState';

import { GetStaffPortalStore } from 'SessionState/Staff/StaffPortalStore';

import JudgePreferencesModal from 'layout/right-buttons/JudgePreferencesModal';

import JudgeTabulatorHelpMessage from 'views/judgeportal/JudgeTabulatorHelpMessage';

type StaffNavIconMenuProps = {
  onHome?: () => void;

  onLogout?: () =>
    void | Promise<void>;
};

const StaffNavIconMenu: React.FC<
  StaffNavIconMenuProps
> = ({
  onHome,
  onLogout,
}) => {
  const { color } =
    useSelector(
      (state: IReduxState) =>
        state.settings,
    );

  const dispatch =
    useDispatch();

  const history =
    useHistory();

  const staffStore =
    GetStaffPortalStore();

  const tabulator =
    staffStore.CurrentTabulator;

  const dashboard =
    staffStore.ReadOnlyDashboard;

  const isLoggedIn =
    !!tabulator;

  const accessLevel =
    tabulator?.accessLevel ??
    '';

  const accessCode =
    tabulator?.accessCodeLast4 ??
    '';

  const eventName =
    dashboard?.eventName ??
    '';

  const overallProgress =
    Number(
      dashboard
        ?.overallProgressPercent ??
        0,
    );

  const [
    isShowSettingsModal,
    setIsShowSettingsModal,
  ] = useState(false);

  const [
    showHelpMessageModal,
    setShowHelpMessageModal,
  ] = useState(false);

  const showSettingsModal =
    (): void => {
      setIsShowSettingsModal(
        true,
      );
    };

  const closeSettingsModal =
    (): void => {
      setIsShowSettingsModal(
        false,
      );

      document.documentElement.click();
    };

  const handleHome =
    (): void => {
      if (onHome) {
        onHome();

        return;
      }

      history.push(
        '/staff/dashboard',
      );
    };

  const handleLogout =
    async (): Promise<void> => {
      if (onLogout) {
        await onLogout();
      } else {
        staffStore.logoutTabulator();
      }

      history.push(
        '/staff/login',
      );
    };

  const handleToggleTheme =
    (): void => {
      dispatch(
        settingsChangeColor(
          color.includes(
            'light',
          )
            ? color.replace(
                'light',
                'dark',
              )
            : color.replace(
                'dark',
                'light',
              ),
        ),
      );
    };

  if (!isLoggedIn) {
    return (
      <div className="fw-bold fs-5 text-body">
        TABULATOR PORTAL
      </div>
    );
  }

  return (
    <>
      {/* ================================================= */}
      {/* TABULATOR INFO */}
      {/* ================================================= */}

      <div className="d-none d-md-flex flex-column align-items-end justify-content-center me-2">
        <div className="fw-bold">
          {accessLevel === 'Admin'
            ? 'Admin Tabulator'
            : 'View Only Staff Dashboard'}
        </div>

        <div className="small text-muted">
          {accessCode && (
            <span>
              Code: {accessCode}
            </span>
          )}
        </div>
      </div>

      {/* ================================================= */}
      {/* USER MENU */}
      {/* ================================================= */}

      <Dropdown align="end">
        <Dropdown.Toggle
          variant="link"
          className="p-1 text-body border-0 shadow-none"
        >
          <CsLineIcons
            icon="user"
            size="20"
          />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {/* ============================================= */}
          {/* SESSION INFO */}
          {/* ============================================= */}

          <div className="px-3 py-2">
            <div className="fw-bold">
              {accessLevel ===
              'Admin'
                ? 'Admin Tabulator'
                : 'View Only Staff Dashboard'}
            </div>

            {accessCode && (
              <div className="small text-muted">
                Access Code:{' '}
                {accessCode}
              </div>
            )}

            {eventName && (
              <div className="fw-bold text-truncate mt-2">
                {eventName}
              </div>
            )}
          </div>

          {/* ============================================= */}
          {/* OVERALL PROGRESS */}
          {/* ============================================= */}

          {dashboard && (
            <>
              <Dropdown.Divider />

              <div className="px-3 py-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small fw-bold">
                    Event Progress
                  </span>

                  <span className="small fw-bold">
                    {overallProgress.toFixed(
                      0,
                    )}
                    %
                  </span>
                </div>

                <ProgressBar
                  now={
                    overallProgress
                  }
                  variant={
                    overallProgress >=
                    100
                      ? 'success'
                      : overallProgress >
                        0
                      ? 'warning'
                      : 'secondary'
                  }
                  style={{
                    height: 4,
                  }}
                />

                <div className="small text-muted mt-1">
                  {
                    dashboard.completedRequiredScores ??
                    0
                  }
                  {' / '}
                  {
                    dashboard.totalRequiredScores ??
                    0
                  }{' '}
                  scoring tasks
                </div>
              </div>
            </>
          )}

          <Dropdown.Divider />

          {/* ============================================= */}
          {/* HOME */}
          {/* ============================================= */}

          <Dropdown.Item
            onClick={
              handleHome
            }
          >
            <CsLineIcons
              icon="home"
              size="16"
              className="me-2"
            />

            Dashboard
          </Dropdown.Item>

          {/* ============================================= */}
          {/* HELP */}
          {/* ============================================= */}

          <Dropdown.Item
            onClick={() =>
              setShowHelpMessageModal(
                true,
              )
            }
          >
            <CsLineIcons
              icon="help"
              size="16"
              className="me-2"
            />

            Message for Help
          </Dropdown.Item>

          {/* ============================================= */}
          {/* VISUAL PREFERENCES */}
          {/* ============================================= */}

          <Dropdown.Item
            onClick={
              showSettingsModal
            }
          >
            <CsLineIcons
              icon="moon"
              size="16"
              className="me-2"
            />

            Visual Preferences
          </Dropdown.Item>

          {/* OPTIONAL QUICK THEME TOGGLE */}

          <Dropdown.Item
            onClick={
              handleToggleTheme
            }
          >
            <CsLineIcons
              icon="light-on"
              size="16"
              className="me-2"
            />

            Toggle Theme
          </Dropdown.Item>

          <Dropdown.Divider />

          {/* ============================================= */}
          {/* LOGOUT */}
          {/* ============================================= */}

          <Dropdown.Item
            className="text-danger"
            onClick={
              handleLogout
            }
          >
            <CsLineIcons
              icon="logout"
              size="16"
              className="me-2"
            />

            Log Out
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* ================================================= */}
      {/* MODALS */}
      {/* ================================================= */}

      <JudgePreferencesModal
        show={
          isShowSettingsModal
        }
        handleClose={
          closeSettingsModal
        }
      />

      <JudgeTabulatorHelpMessage
        show={
          showHelpMessageModal
        }
        onHide={() =>
          setShowHelpMessageModal(
            false,
          )
        }
        mode="login-help"
        initialMessage="I need help with the tabulator portal."
      />
    </>
  );
};

export default React.memo(
  StaffNavIconMenu,
);